import { DateTime } from 'luxon';

export type DateTimeFieldInput = Date | DateTime | string | null | undefined;

export function toDateOnly(value: DateTimeFieldInput): string | null {
    if (!value) return null;

    if (DateTime.isDateTime(value)) {
        return value.isValid ? value.toISODate() : null;
    }

    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return null;
        return DateTime.fromJSDate(value).toISODate();
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;

        const dateOnlyMatch = trimmed.match(/^\d{4}-\d{2}-\d{2}/);
        if (dateOnlyMatch) return dateOnlyMatch[0];

        const parsed = DateTime.fromISO(trimmed);
        return parsed.isValid ? parsed.toISODate() : trimmed;
    }

    return null;
}

export function toTimeOnly(value: DateTimeFieldInput): string | null {
    if (!value) return null;

    if (DateTime.isDateTime(value)) {
        return value.isValid ? value.toFormat('HH:mm:ss') : null;
    }

    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return null;
        return DateTime.fromJSDate(value).toFormat('HH:mm:ss');
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;

        const timeOnlyMatch = trimmed.match(/^(\d{1,2}):([0-5]\d)(?::([0-5]\d))?/);
        if (timeOnlyMatch) {
            const hour = timeOnlyMatch[1].padStart(2, '0');
            const minute = timeOnlyMatch[2];
            const second = timeOnlyMatch[3] ?? '00';

            return `${hour}:${minute}:${second}`;
        }

        const parsed = DateTime.fromISO(trimmed);
        return parsed.isValid ? parsed.toFormat('HH:mm:ss') : trimmed;
    }

    return null;
}

export function toDateOnlyFields<T extends Record<string, unknown>>(
    value: T,
    fields: Array<keyof T | string>
): T {
    const next = { ...value } as Record<string, unknown>;

    fields.forEach((field) => {
        const key = String(field);
        if (key in next) {
            next[key] = toDateOnly(next[key] as DateTimeFieldInput);
        }
    });

    return next as T;
}

export function toTimeOnlyFields<T extends Record<string, unknown>>(
    value: T,
    fields: Array<keyof T | string>
): T {
    const next = { ...value } as Record<string, unknown>;

    fields.forEach((field) => {
        const key = String(field);
        if (key in next) {
            next[key] = toTimeOnly(next[key] as DateTimeFieldInput);
        }
    });

    return next as T;
}
